import {
  UPDATE_ASSESSMENT_MARKING_LIST,
  TOGGLE_ASSESSMENT_MARKING_LIST_LOADING,
  TOGGLE_ASSESSMENT_MARKING_IS_SAVING,
  ASSESSMENT_MARKING_LIST_ERROR,
  UPDATE_ASSESSMENT_MARKING_ANSWER,
  UPDATE_ASSESSMENT_MARKING_RECORD,
  RESET_HAS_CHANGED_ASSESSMENT_MARKING_LIST,
  RESET_ASSESSMENT_MARKING_STATE,
  UPDATE_ASSESSMENT_MARKING_TOTAL_LIST,
  SET_ASSESSMENT_MARKING_ROUNDS,
  SET_ASSESSMENT_MARKING_SELECTED_ROUND
} from '../actions/dataEntryAssessmentMarkingActions'

const initState = {
  name: 'Data Entry Assessment Marking',
  marking: {
    loading: true,
    isSaving: false,
    list: [],
    error: null,
    totalRecords: 0
  },
  saveList: [],
  // MF-399 — rounds/milestones. rounds vacío = assessment legacy (path plano);
  // selectedRoundId null => las celdas leen/escriben question.answer directo.
  rounds: [],
  carryForward: true,
  selectedRoundId: null
}
const modelRecord = {
  _id: '',
  code: '',
  comments: '',
  date: null,
  questions: []
}

const ROUND_STATUS_FINALIZED = 'finalized'

// MF-399 — proyecta el answer/markingDoneBy del round seleccionado en el campo
// plano question.answer que consumen las celdas. Preserva question.rounds para
// poder escribir y cambiar de round. Sin roundId (legacy) => devuelve tal cual.
const projectQuestionRound = (question, roundId) => {
  if (!roundId) return question
  const entry = (question.rounds || []).find((r) => String(r.roundId) === String(roundId))
  return {
    ...question,
    answer: entry?.answer ? [...entry.answer] : [],
    markingDoneBy: entry?.markingDoneBy ?? null,
    carriedFrom: entry?.carriedFrom ?? null
  }
}

// Devuelve una lista NUEVA (refs nuevas) con los answers proyectados al round —
// las refs nuevas son necesarias para que las celdas memoizadas re-rendericen al
// cambiar de round (useEffect([answer]) resetea el valor local).
const projectListRound = (list = [], roundId) =>
  list.map((record) => ({
    ...record,
    questions: (record.questions || []).map((q) => projectQuestionRound(q, roundId))
  }))

// Default: PRIMER round NO finalizado (empuja a completar en orden, R1 antes que
// R2); si están todos finalizados, el último.
const pickDefaultRoundId = (rounds = []) => {
  if (!rounds.length) return null
  const active = rounds.find((r) => r.status !== ROUND_STATUS_FINALIZED)
  return String((active || rounds[rounds.length - 1])._id)
}

const dataEntryAssessmentMarkingReducer = (state = initState, action) => {
  const newState = { ...state }

  switch (action.type) {
    case UPDATE_ASSESSMENT_MARKING_LIST: {
      const roundId = newState.selectedRoundId
      newState.marking.list = action.payload.value.map((record) => {
        // Proyecta primero el round seleccionado en question.answer (legacy: noop).
        const projectedQuestions = (record.questions || []).map((q) => projectQuestionRound(q, roundId))
        const find = newState.saveList.find((r) => r._id === record._id)
        if (find) {
          return {
            ...record,
            ...find,
            hasChanged: true,
            questions: projectedQuestions.map((question) => {
              // Edits pendientes: solo aplican al round al que pertenecen.
              const fq = find.questions.find(
                (q) =>
                  q.questionId === question.questionId && String(q.roundId || '') === String(roundId || '')
              )
              if (fq) {
                return { ...question, answer: [...(fq.answer || [])], hasChanged: true }
              }
              return { ...question, hasChanged: false }
            })
          }
        }
        return {
          ...record,
          hasChanged: false,
          questions: projectedQuestions.map((question) => ({
            ...question,
            hasChanged: false
          }))
        }
      })
      break
    }
    case UPDATE_ASSESSMENT_MARKING_ANSWER: {
      const { recordId, code, questionId, answer } = action.payload.value
      // MF-399 — el round se toma del estado (las celdas no lo conocen). Sin
      // round (legacy) => se escribe question.answer plano, saveList sin roundId.
      const roundId = newState.selectedRoundId

      const studentIndex = newState.marking.list.findIndex((student) => student.code === code)
      if (studentIndex !== -1) {
        const questionIndex = newState.marking.list[studentIndex].questions.findIndex(
          (question) => question.questionId === questionId
        )

        if (questionIndex !== -1) {
          const question = newState.marking.list[studentIndex].questions[questionIndex]
          question.answer = [...answer]
          question.hasChanged = true
          if (roundId) {
            // Escribe al round entry (fuente de verdad al cambiar de round).
            const rounds = question.rounds || []
            const has = rounds.some((r) => String(r.roundId) === String(roundId))
            question.rounds = has
              ? rounds.map((r) => (String(r.roundId) === String(roundId) ? { ...r, answer: [...answer] } : r))
              : [...rounds, { roundId, answer: [...answer], markingDoneBy: null }]
          }
          newState.marking.list[studentIndex].hasChanged = true
        }
      }
      // saveList: la question lleva roundId cuando hay round → el BE escribe al
      // round correcto (updateBulkMarking usa arrayFilters por roundId).
      const sameQ = (q) => q.questionId === questionId && String(q.roundId || '') === String(roundId || '')
      const questionEntry = roundId ? { questionId, answer, roundId } : { questionId, answer }
      const find = newState.saveList.find((record) => record._id === recordId)
      if (find) {
        newState.saveList = newState.saveList.map((record) => {
          const questions = record.questions
          if (record._id === recordId) {
            const fq = questions.find(sameQ)
            if (fq) {
              return {
                ...record,
                questions: questions.map((q) => (sameQ(q) ? { ...q, answer: [...answer] } : q))
              }
            } else {
              questions.push(questionEntry)
            }
          }
          return {
            ...record,
            questions
          }
        })
      } else {
        newState.saveList.push({
          ...modelRecord,
          _id: recordId,
          code,
          questions: [questionEntry]
        })
      }
      break
    }

    case UPDATE_ASSESSMENT_MARKING_RECORD: {
      const { _id, ...updateData } = action.payload.value
      const recordIndex = newState.marking.list.findIndex((record) => record._id === _id)

      if (recordIndex !== -1) {
        newState.marking.list[recordIndex] = {
          ...newState.marking.list[recordIndex],
          ...updateData,
          hasChanged: true
        }
      }
      const find = newState.saveList.find((record) => record._id === _id)
      if (find) {
        newState.saveList = newState.saveList.map((record) =>
          record._id === _id ? { ...record, ...updateData } : record
        )
      } else {
        newState.saveList.push({ ...modelRecord, _id, ...updateData })
      }

      return newState
    }

    case TOGGLE_ASSESSMENT_MARKING_LIST_LOADING:
      newState.marking.loading = action.payload.value
      break

    case TOGGLE_ASSESSMENT_MARKING_IS_SAVING:
      newState.marking.isSaving = action.payload.value
      break

    case ASSESSMENT_MARKING_LIST_ERROR:
      newState.marking.error = action.payload.value
      break

    case RESET_HAS_CHANGED_ASSESSMENT_MARKING_LIST:
      // Mutate flags in-place to avoid creating new array references for
      // student.questions — a new reference would cascade through
      // useCustomColumns → tableColumns → DataGrid and reset column visibility.
      newState.marking.list.forEach((student) => {
        student.hasChanged = false
        student.questions.forEach((question) => {
          question.hasChanged = false
        })
      })
      newState.saveList = []
      break
    case RESET_ASSESSMENT_MARKING_STATE:
      newState.marking = { ...initState.marking }
      newState.saveList = []
      newState.rounds = []
      newState.carryForward = true
      newState.selectedRoundId = null
      break

    case UPDATE_ASSESSMENT_MARKING_TOTAL_LIST:
      newState.marking.totalRecords = action.payload.value
      break

    case SET_ASSESSMENT_MARKING_ROUNDS: {
      const { rounds = [], carryForward = true } = action.payload
      newState.rounds = rounds
      newState.carryForward = carryForward ?? true
      // Fija un round por defecto si el actual ya no es válido (o no hay).
      const stillValid =
        rounds.length && rounds.some((r) => String(r._id) === String(newState.selectedRoundId))
      newState.selectedRoundId = stillValid ? newState.selectedRoundId : pickDefaultRoundId(rounds)
      break
    }

    case SET_ASSESSMENT_MARKING_SELECTED_ROUND: {
      const { roundId } = action.payload
      newState.selectedRoundId = roundId
      // Re-proyecta la lista existente al nuevo round (sin ir al servidor).
      newState.marking = {
        ...newState.marking,
        list: projectListRound(newState.marking.list, roundId)
      }
      break
    }
    default:
      break
  }
  return newState
}

export default dataEntryAssessmentMarkingReducer
